import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import type { AssociationKind } from 'hooks/api'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import type { ContactOrganization, ContactSchoolLocation, ContactVendorLocation } from 'types/contacts'

type AnyAssociation = ContactSchoolLocation | ContactVendorLocation | ContactOrganization

function getExternalId(assoc: AnyAssociation): string {
  if ('ordoSchoolLocationId' in assoc) {
    return assoc.ordoSchoolLocationId
  }
  if ('ordoVendorLocationId' in assoc) {
    return assoc.ordoVendorLocationId
  }
  return assoc.ordoOrganizationId
}

function formatBadgeLabel(assoc: AnyAssociation): string {
  const id = getExternalId(assoc)
  if (assoc.role) {
    return `${assoc.role}:${id}`
  }
  return id
}

interface AssociationSectionProps {
  label: string
  kind: AssociationKind
  items: AnyAssociation[]
  editing: boolean
  onAdd: (kind: AssociationKind, externalId: string, role: string) => Promise<void>
  onUpdate: (kind: AssociationKind, id: string, role: string) => Promise<void>
  onRemove: (kind: AssociationKind, id: string) => Promise<void>
}

function AssociationSection({ label, kind, items, editing, onAdd, onUpdate, onRemove }: AssociationSectionProps) {
  const [adding, setAdding] = useState(false)
  const [newExternalId, setNewExternalId] = useState('')
  const [newRole, setNewRole] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editRole, setEditRole] = useState('')

  const handleAdd = useCallback(async () => {
    if (!newExternalId.trim() || !newRole.trim()) {
      return
    }
    await onAdd(kind, newExternalId.trim(), newRole.trim())
    setNewExternalId('')
    setNewRole('')
    setAdding(false)
  }, [kind, newExternalId, newRole, onAdd])

  const handleUpdate = useCallback(
    async (id: string) => {
      if (!editRole.trim()) {
        return
      }
      await onUpdate(kind, id, editRole.trim())
      setEditingId(null)
    },
    [kind, editRole, onUpdate],
  )

  const handleStartEdit = useCallback((assoc: AnyAssociation) => {
    setEditingId(assoc.id)
    setEditRole(assoc.role)
  }, [])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-muted-foreground">
          {label} ({items.length})
        </label>
        {editing && !adding && (
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      {items.length === 0 && !adding && <p className="text-sm text-muted-foreground">None</p>}

      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((assoc) => (
            <div key={assoc.id} className="flex items-center gap-1">
              {editing && editingId === assoc.id ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    placeholder="Role"
                    className="h-7 w-28 text-xs"
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleUpdate(assoc.id)}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {formatBadgeLabel(assoc)}
                  </Badge>
                  {editing && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleStartEdit(assoc)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => onRemove(kind, assoc.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">External ID</label>
            <Input value={newExternalId} onChange={(e) => setNewExternalId(e.target.value)} className="h-8 text-sm" />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">Role</label>
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} className="h-8 text-sm" />
          </div>
          <Button type="button" size="sm" onClick={handleAdd}>
            Add
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setAdding(false)
              setNewExternalId('')
              setNewRole('')
            }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

export function ContactAssociations({
  schoolLocations,
  vendorLocations,
  organizations,
  editing,
  onAdd,
  onUpdate,
  onRemove,
}: {
  schoolLocations: ContactSchoolLocation[]
  vendorLocations: ContactVendorLocation[]
  organizations: ContactOrganization[]
  editing: boolean
  onAdd: (kind: AssociationKind, externalId: string, role: string) => Promise<void>
  onUpdate: (kind: AssociationKind, id: string, role: string) => Promise<void>
  onRemove: (kind: AssociationKind, id: string) => Promise<void>
}) {
  return (
    <div className="space-y-4">
      <Separator />
      <AssociationSection
        label="School Locations"
        kind="school-locations"
        items={schoolLocations}
        editing={editing}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
      <AssociationSection
        label="Vendor Locations"
        kind="vendor-locations"
        items={vendorLocations}
        editing={editing}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
      <AssociationSection
        label="Organizations"
        kind="organizations"
        items={organizations}
        editing={editing}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onRemove={onRemove}
      />
    </div>
  )
}
